import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextInput from '@common/components/Inputs/TextInput';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store';
import SelectInput from '../../forms/SelectInput';
import MenuItem from '@mui/material/MenuItem';
import { IconButton, SelectChangeEvent } from '@mui/material';
import { stringToColor } from '../../../utils/avatarStyles';
import ValidationResult from '@common/interfaces/api/validation-result';
import SoftwareVersion from '../../../models/enums/software-version';
import { SearchIcon } from '@common/utils/icons/icons';
import {
  softwareVersionStorageName,
  toggleBuildNumberAndVersion,
  updateMissionFilters,
} from '../../../store/service/service-slice';
import { selectServiceMissionsData } from '../../../store/service/service-slice-selectors';
import SwitchInput from '../../forms/SwitchInput';

const BuildNumberFilters: React.FC<{
  searchBuildNumber: string;
  onSearchBuildNumberChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  searchBuildNumberValidationResult: ValidationResult;
  repeatedDeployments: { value: boolean; onToggle: () => void };
}> = ({
  searchBuildNumber,
  onSearchBuildNumberChange,
  searchBuildNumberValidationResult,
  repeatedDeployments,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const missionStatsData = useAppSelector((state) => selectServiceMissionsData(state));

  useEffect(() => {
    const softwareVersion = localStorage.getItem(softwareVersionStorageName);
    if (softwareVersion != null) {
      dispatch(updateMissionFilters({ softwareVersion: softwareVersion as SoftwareVersion }));
    }
  }, [dispatch]);

  const searchBuildNumberClickHandler = (event: React.FormEvent) => {
    event.preventDefault();
    if (searchBuildNumber.length > 0) {
      dispatch(
        toggleBuildNumberAndVersion({
          buildNumber: searchBuildNumber,
          softwareVersion: missionStatsData.filters.softwareVersion,
        }),
      );
    }
  };

  const softwareVersionChangeHandler = (event: SelectChangeEvent<unknown>) => {
    dispatch(updateMissionFilters({ softwareVersion: event.target.value as SoftwareVersion }));
  };

  return (
    <React.Fragment>
      <Grid item xs={12} pr={1}>
        <form onSubmit={searchBuildNumberClickHandler}>
          <Stack direction="row" spacing={2} alignContent="top">
            <Box maxWidth="sm">
              <TextInput
                label={t('input-label-build-number')}
                type="string"
                size="small"
                value={searchBuildNumber}
                onChange={onSearchBuildNumberChange}
                validationResult={searchBuildNumberValidationResult}
                endIcon={
                  <IconButton type="submit">
                    <SearchIcon />
                  </IconButton>
                }
              />
            </Box>
            <Box sx={(theme) => ({ minWidth: theme.spacing(24) })}>
              <SelectInput
                label={t('input-label-software-version')}
                value={missionStatsData.filters.softwareVersion}
                onChange={softwareVersionChangeHandler}
                size="small"
              >
                {Object.values(SoftwareVersion).map((v) => (
                  <MenuItem key={v} value={v}>
                    <Box
                      sx={(theme) => ({
                        display: 'inline-block',
                        mr: 2,
                        width: theme.spacing(1.5),
                        height: theme.spacing(1.5),
                        borderRadius: theme.spacing(0.75),
                        backgroundColor: stringToColor(v),
                        overflow: 'hidden',
                      })}
                    >
                      <Box
                        sx={{
                          backgroundColor: 'black',
                          width: '100%',
                          height: '100%',
                          opacity: 0.2,
                        }}
                      />
                    </Box>
                    {v}
                  </MenuItem>
                ))}
              </SelectInput>
            </Box>
            <SwitchInput
              label={t('input-label-show-only-repeated-deployments')}
              isChecked={repeatedDeployments.value}
              onChange={repeatedDeployments.onToggle}
            />
          </Stack>
        </form>
      </Grid>
    </React.Fragment>
  );
};

export default BuildNumberFilters;
